import React, { useState } from "react";
import { Eye, EyeOff, Shield, Lock, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { CHANGE_PASSWORD } from "../../../api/apiFunction/authServices";
import { deleteCurrentUserAccount } from "../../../api/apiFunction/userServices";
import ConfirmationModal from "./ConfirmationModal";

const SecuritySettings = ({ userData }) => {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitPasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation
    if (!passwordData.oldPassword) {
      toast.error("Please enter your current password");
      setLoading(false);
      return;
    }

    if (!passwordData.newPassword) {
      toast.error("Please enter a new password");
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const response = await CHANGE_PASSWORD({
        currentPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      if (response?.status === 200) {
        toast.success("Password changed successfully!");
        setPasswordData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setShowChangePassword(false);
      } else if (response?.status === 401) {
        toast.error(response?.data?.error || "Current password is incorrect");
      } else if (response?.status === 400) {
        toast.error(response?.data?.error || "Invalid password format");
      } else {
        toast.error(response?.data?.error || "Failed to change password");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const response = await deleteCurrentUserAccount();

      if (response?.status === 200) {
        toast.success(
          response?.data?.message ||
            "Your account has been deleted successfully."
        );
        setShowDeleteModal(false);
        // Clear local user data and redirect to landing/login
        localStorage.clear();
        window.location.href = "/";
        return;
      }

      // Handle outstanding Stripe balance case
      if (
        response?.status === 400 &&
        response?.data?.outstandingBalance !== undefined
      ) {
        const amount = Number(response.data.outstandingBalance || 0).toFixed(2);
        toast.error(
          response?.data?.message ||
            `You have an outstanding Stripe balance of $${amount}. Please settle it before deleting your account.`
        );
        setShowDeleteModal(false);
        return;
      }

      toast.error(
        response?.data?.message || "Failed to delete account. Please try again."
      );
    } catch (error) {
      toast.error("An unexpected error occurred while deleting your account.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Password Section */}
      <div className="p-3 md:p-4 bg-bg-60 rounded-lg mt-2 md:mt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-ac-02/20 rounded-md">
              <Lock size={18} strokeWidth={1.5} className="text-ac-02" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-fg-50">Password</h3>
              {/* <p className="text-xs text-fg-60">Last changed 3 months ago</p> */}
            </div>
          </div>
          {!showChangePassword && (
            <button
              onClick={() => setShowChangePassword(true)}
              className="px-3 py-2 md:py-1.5 bg-ac-02 hover:bg-ac-01 text-white dark:text-black rounded-full text-xs font-medium transition-colors touch-manipulation w-full md:w-auto"
            >
              Change
            </button>
          )}
        </div>

        {showChangePassword && (
          <form
            onSubmit={handleSubmitPasswordChange}
            className="space-y-3 pt-6"
          >
            {/* Current Password */}
            <div>
              <label className="block text-xs font-medium text-fg-50 mb-1.5">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showOldPassword ? "text" : "password"}
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  className="w-full px-3 py-2 pr-10 bg-bg-50 rounded-md text-fg-50 placeholder-fg-70 text-sm focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showOldPassword ? (
                    <EyeOff
                      size={16}
                      className="text-fg-60"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <Eye size={16} className="text-fg-60" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-xs font-medium text-fg-50 mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  className="w-full px-3 py-2 pr-10 bg-bg-50 rounded-md text-fg-50 placeholder-fg-70 text-sm focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showNewPassword ? (
                    <EyeOff
                      size={16}
                      className="text-fg-60"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <Eye size={16} className="text-fg-60" strokeWidth={1.5} />
                  )}
                </button>
              </div>
              {passwordData.newPassword &&
                passwordData.newPassword.length < 6 && (
                  <p className="text-xs text-amber-400 mt-1">
                    At least 6 characters required
                  </p>
                )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-xs font-medium text-fg-50 mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm password"
                  className={`w-full px-3 py-2 pr-10 bg-bg-50 rounded-md text-fg-50 placeholder-fg-70 text-sm focus:outline-none focus:ring-1 transition-all ${
                    passwordData.confirmPassword &&
                    passwordData.newPassword !== passwordData.confirmPassword
                      ? "border-red-400 focus:ring-red-400"
                      : "border-bd-50 focus:ring-ac-02"
                  }`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showConfirmPassword ? (
                    <EyeOff
                      size={16}
                      className="text-fg-60"
                      strokeWidth={1.5}
                    />
                  ) : (
                    <Eye size={16} className="text-fg-60" strokeWidth={1.5} />
                  )}
                </button>
              </div>
              {passwordData.confirmPassword &&
                passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-xs text-red-400 mt-1">
                    Passwords do not match
                  </p>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse md:flex-row gap-2 pt-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordData({
                    oldPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                }}
                className="px-3 py-2 md:py-1.5 border border-bd-50 text-fg-50 hover:bg-bg-50 rounded-full text-xs font-medium transition-colors touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-3 py-2 md:py-1.5 bg-ac-02 hover:bg-ac-01 disabled:bg-ac-02/50 disabled:cursor-not-allowed text-white dark:text-black rounded-full text-xs font-medium transition-colors touch-manipulation"
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Dangerous Zone - Delete Account */}
      <div className="mt-6 md:mt-8 rounded-xl border border-red-200 bg-bg-60/80 px-3 md:px-4 py-3 md:py-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-600 border border-red-200 flex-shrink-0">
              <Trash2 size={18} strokeWidth={1.5} />
            </div>
            <div className="space-y-1.5 flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-2 py-0.5 border border-red-100">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-red-700">
                  Danger zone
                </span>
              </div>

              <h3 className="text-sm font-semibold text-red-700">
                Delete account
              </h3>
              <p className="text-xs text-fg-60 max-w-md leading-relaxed">
                Deleting your account is{" "}
                <span className="font-semibold text-red-700">permanent</span>{" "}
                and cannot be undone. You'll lose access to Leeb AI, your
                history, and any saved data, according to our data retention
                policy.
              </p>

              <ul className="mt-2 space-y-1.5 text-xs text-fg-60">
                <li className="flex gap-2">
                  <span className="mt-0.5 h-1 w-1 rounded-full bg-red-400 flex-shrink-0" />
                  <span>
                    Any active subscriptions will be cancelled in Stripe before
                    deletion.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-0.5 h-1 w-1 rounded-full bg-red-400 flex-shrink-0" />
                  <span>
                    You will lose access to all services, history, and usage
                    data.
                  </span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-0.5 h-1 w-1 rounded-full bg-red-400 flex-shrink-0" />
                  <span>
                    If Stripe reports any outstanding balance, deletion will be
                    blocked until payments are settled.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="mt-2 md:mt-1 inline-flex items-center justify-center rounded-full border border-red-300 bg-white px-3.5 py-2 md:py-1.5 text-xs font-semibold text-red-700 shadow-sm hover:bg-red-50 hover:border-red-400 transition-colors touch-manipulation w-full md:w-auto"
          >
            Delete account
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => !deleteLoading && setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        title="Delete your account?"
        message="Account deletion is permanent and cannot be undone."
        warningMessage="If Stripe reports any unpaid invoices or outstanding balance, your account cannot be deleted until the payment is settled. This action will cancel your Stripe subscriptions and remove your access to Leeb AI."
        confirmText="Yes, delete my account"
        cancelText="Cancel"
        confirmButtonClass="border border-red-400/70 bg-red-500/10 text-red-400 hover:bg-red-500/20"
        loading={deleteLoading}
      />
    </div>
  );
};

export default SecuritySettings;
